import { resupplyConfigFor } from '@1delta/data-sdk'
import { parseRawAmount } from '../../../utils/parsing'
import { isFailedCall } from '../../../utils/multicall'
import { createMarketUid } from '../../../utils'
import { getDisplayPrice, getOraclePrice } from '../utils/oraclePrice'
import { createMultiAccountTypeUserState } from '../utils'
import {
  LenderCrossPoolMeta,
  LenderToLenderCrossPoolMeta,
  UserData,
} from '../utils/types'
import { resupplyLenderKey } from '../../public-data/resupply/convertPublic'
import {
  RESUPPLY_CALLS_PER_PAIR,
  getCachedResupplyDiscovery,
} from './userCallBuild'

/** Per-position Resupply detail attached to the debt row (raw strings). */
export interface ResupplyPositionInfo {
  /** Internal borrow shares. NOT an amount — see `debt` for reUSD units. */
  borrowShares: string
  /** Collateral in VAULT SHARES (the pair's own accounting unit). */
  collateralShares: string
  /** `convertToAssets(1e18)` on the collateral vault at read time — the
   *  factor that turned those shares into the reported underlying. ~1e15 for
   *  Curve Lend vaults. */
  collateralSharePrice: string
  /** The pair contract — the market id and every write target. */
  pair: string
  /** The collateral vault (Curve Lend / Fraxlend 4626 share token). */
  collateralVault: string
}

const big = (v: any): bigint => {
  try {
    if (typeof v === 'bigint') return v
    if (typeof v === 'number' || typeof v === 'string') return BigInt(v)
  } catch {}
  return 0n
}

const tupleAt = (v: any, i: number): bigint =>
  Array.isArray(v) ? big(v[i]) : 0n

const ONE = 10n ** 18n

/**
 * The contract's `VaultAccount.toAmount(shares, roundUp)` — reproduced rather
 * than called, because `getPairAccounting` already gave us both totals with
 * interest previewed. Round UP, matching how the pair itself values a
 * borrower's debt (`_isSolvent`, `repay`).
 */
function sharesToAmount(
  shares: bigint,
  totalAmount: bigint,
  totalShares: bigint,
): bigint {
  if (totalShares === 0n || shares === 0n) return 0n
  const q = (shares * totalAmount) / totalShares
  return q * totalShares < shares * totalAmount ? q + 1n : q
}

/**
 * Returns a [converter, expectedCallCount] tuple for Resupply user data.
 *
 * Layout mirrors `buildResupplyUserCall` (deterministic from the cached
 * roster). Emits one `UserData` per pair key `RESUPPLY_<chainId>_<PAIR>`, each
 * with a single sub-account `'0'` — positions are address-keyed, at most ONE
 * per user per pair.
 *
 * Both rows are denominated the way the user experiences them: the collateral
 * row in the UNDERLYING (crvUSD / frxUSD), converted from vault shares with
 * the vault's own `convertToAssets`, and the debt row in reUSD converted from
 * borrow shares with the pair's totals. The raw share figures ride along in
 * `resupplyInfo` because every write on the pair — `removeCollateralVault`,
 * `repay`, `repayWithCollateral` — is denominated in SHARES, so a caller that
 * only has the human numbers cannot build a transaction.
 */
export const getResupplyUserDataConverter = (
  lender: string,
  chainId: string,
  account: string,
  meta?: LenderToLenderCrossPoolMeta,
): [(data: any[]) => { [lender: string]: UserData } | undefined, number] => {
  const cfg = resupplyConfigFor(lender, chainId)
  // The SAME active-pair list the build phase discovered — re-discovering here
  // could desync the slicer from the call list, and the snapshots themselves
  // were already read during the build (they are far too expensive to batch
  // across the whole roster; see `userCallBuild`).
  const discovery = getCachedResupplyDiscovery(chainId, lender, account)
  const positions = discovery?.positions ?? []
  const expected =
    cfg && positions.length > 0 ? positions.length * RESUPPLY_CALLS_PER_PAIR : 0

  return [
    (data: any[]) => {
      if (!cfg || expected === 0) return undefined
      if (!data || data.length < expected) return undefined

      const debtAddr = cfg.reusd.toLowerCase()
      const debtDecimals = cfg.debtDecimals ?? 18
      const out: { [lender: string]: UserData } = {}

      positions.forEach(
        ({ pair, collateral, borrowShares, collateralShares }, i) => {
          const base = i * RESUPPLY_CALLS_PER_PAIR
          const accounting = data[base]
          // A failed accounting read is NOT an empty position — skip the pair
          // rather than reporting a zeroed one.
          if (isFailedCall(accounting) || !Array.isArray(accounting)) return

          const totalBorrowAmount = tupleAt(accounting, 1)
          const totalBorrowShares = tupleAt(accounting, 2)
          const sharePrice = isFailedCall(data[base + 1])
            ? 0n
            : big(data[base + 1])

          const lenderKey = resupplyLenderKey(lender, chainId, pair)
          const metaMap: LenderCrossPoolMeta | undefined = meta?.[lenderKey]
          if (!metaMap) return

          const debt = sharesToAmount(
            borrowShares,
            totalBorrowAmount,
            totalBorrowShares,
          )
          // Shares → underlying, the same conversion the public converter does.
          const collUnderlying =
            sharePrice > 0n ? (collateralShares * sharePrice) / ONE : 0n

          // The collateral row is keyed on the UNDERLYING, matching public data.
          const collAddrEntry = Object.values(metaMap).find(
            (m: any) =>
              m?.underlying && m.underlying.toLowerCase() !== debtAddr,
          ) as any
          const collAddr = collAddrEntry?.underlying?.toLowerCase()
          if (!collAddr) return

          const collUid = createMarketUid(chainId, lenderKey, collAddr)
          const loanUid = createMarketUid(chainId, lenderKey, debtAddr)
          const collMeta = metaMap[collUid]
          const loanMeta = metaMap[loanUid]
          const collDecimals = collMeta?.asset?.decimals ?? 18
          const collDisplay = collMeta ? getDisplayPrice(collMeta) : 0
          const collOracle = collMeta ? getOraclePrice(collMeta) : 0
          const collHist = collMeta?.price?.priceUsd24h ?? collDisplay
          const loanDisplay = loanMeta ? getDisplayPrice(loanMeta) : 0
          const loanOracle = loanMeta ? getOraclePrice(loanMeta) : 0
          const loanHist = loanMeta?.price?.priceUsd24h ?? loanDisplay

          const resupplyInfo: ResupplyPositionInfo = {
            borrowShares: borrowShares.toString(),
            collateralShares: collateralShares.toString(),
            collateralSharePrice: sharePrice.toString(),
            pair,
            collateralVault: collateral,
          }

          const collStr = parseRawAmount(
            collUnderlying.toString(),
            collDecimals,
          )
          const collNum = Number(collStr)
          const debtStr = parseRawAmount(debt.toString(), debtDecimals)
          const debtNum = Number(debtStr)

          const lendingPositions: Record<string, any> = {
            '0': {
              [collUid]: {
                marketUid: collUid,
                underlying: collAddr,
                deposits: collStr,
                debt: '0',
                debtStable: '0',
                depositsUSD: collNum * collDisplay,
                debtUSD: 0,
                debtStableUSD: 0,
                depositsUSDOracle: collNum * collOracle,
                debtUSDOracle: 0,
                debtStableUSDOracle: 0,
                stableBorrowRate: '0',
                collateralEnabled: true,
                claimableRewards: 0,
                resupplyInfo,
              },
              [loanUid]: {
                marketUid: loanUid,
                underlying: debtAddr,
                deposits: '0',
                debt: debtStr,
                debtStable: '0',
                depositsUSD: 0,
                debtUSD: debtNum * loanDisplay,
                debtStableUSD: 0,
                depositsUSDOracle: 0,
                debtUSDOracle: debtNum * loanOracle,
                debtStableUSDOracle: 0,
                stableBorrowRate: '0',
                collateralEnabled: false,
                claimableRewards: 0,
                resupplyInfo,
              },
            },
          }
          const modes: Record<string, number> = { '0': 0 }
          const hist = {
            '0': {
              totalDeposits24h: collNum * collHist,
              totalDebt24h: debtNum * loanHist,
            },
          }

          const userData = createMultiAccountTypeUserState(
            { chainId, account, lendingPositions, modes } as any,
            metaMap,
            hist,
          )
          if (userData && userData.data.length > 0) out[lenderKey] = userData
        },
      )

      return Object.keys(out).length > 0 ? out : undefined
    },
    expected,
  ]
}
